import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiClock, FiRefreshCw, FiArrowLeft, FiChevronLeft, FiChevronRight, FiSearch, FiCopy, FiDownload } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { BULK_UPLOAD_JOB_LIST } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import './Page.css';
import './JobHistory.css';

const PAGE_SIZE = 10;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function num(val) {
  if (typeof val === 'number' && !Number.isNaN(val)) return val;
  const n = Number(val);
  return Number.isNaN(n) ? 0 : n;
}

const INITIAL_COLUMN_PERCENTS = [16, 9, 14, 7, 7, 7, 12, 12, 11];
const MIN_COLUMN_PERCENTS = [5, 5, 10, 5, 5, 5, 8, 8, 8];

function JobHistory() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [columnWidths, setColumnWidths] = useState(INITIAL_COLUMN_PERCENTS);
  const [resizingCol, setResizingCol] = useState(null);
  const resizeStartX = React.useRef(0);
  const resizeStartPercent = React.useRef(0);
  const tableRef = React.useRef(null);
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = async (text, copyKey) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(copyKey);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (_) {
      setCopiedId(null);
    }
  };

  const fetchJobs = async () => {
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(BULK_UPLOAD_JOB_LIST.path), {
        method: BULK_UPLOAD_JOB_LIST.method,
        headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status));
        setJobs([]);
        return;
      }
      const list = data.data || data;
      setJobs(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.message || 'Failed to load job history.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return jobs;
    const q = search.trim().toLowerCase();
    return jobs.filter((j) => (j.job_id || '').toLowerCase().includes(q));
  }, [jobs, search]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(startIndex, startIndex + PAGE_SIZE);
  const displayStart = totalItems === 0 ? 0 : startIndex + 1;
  const displayEnd = totalItems === 0 ? 0 : Math.min(startIndex + PAGE_SIZE, totalItems);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages, totalItems]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleResizeStart = (colIndex, e) => {
    e.preventDefault();
    setResizingCol(colIndex);
    resizeStartX.current = e.clientX;
    resizeStartPercent.current = columnWidths[colIndex];
  };

  useEffect(() => {
    if (resizingCol === null) return;
    const LINK_COLS = [7, 8];
    const onMove = (e) => {
      const tableEl = tableRef.current;
      if (!tableEl) return;
      const tableWidth = tableEl.offsetWidth;
      if (tableWidth <= 0) return;
      const deltaPx = e.clientX - resizeStartX.current;
      const deltaPct = (deltaPx / tableWidth) * 100;
      const newPct = Math.max(
        MIN_COLUMN_PERCENTS[resizingCol],
        Math.min(50, resizeStartPercent.current + deltaPct)
      );
      const diff = newPct - resizeStartPercent.current;
      setColumnWidths((prev) => {
        const next = [...prev];
        next[resizingCol] = newPct;
        const other = resizingCol === 7 ? 8 : 7;
        next[other] = Math.max(MIN_COLUMN_PERCENTS[other], prev[other] - diff);
        return next;
      });
      resizeStartX.current = e.clientX;
      resizeStartPercent.current = newPct;
    };
    const onUp = () => setResizingCol(null);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizingCol]);

  return (
    <div className="page job-history-page">
      <header className="job-history-header">
        <h1 className="job-history-title">Job history</h1>
        <p className="job-history-desc">
          CSV bulk upload jobs with progress and results.
        </p>
      </header>

      <div className="job-history-toolbar">
        <div className="job-history-search-wrap">
          <FiSearch className="job-history-search-icon" aria-hidden />
          <input
            type="text"
            className="job-history-search-input"
            placeholder="Search by job ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search jobs"
          />
        </div>
        <div className="job-history-actions">
          <Link to="/dashboard/cms/bulk-upload" className="job-history-back-link">
            <FiArrowLeft className="job-history-back-icon" aria-hidden />
            Back to Bulk upload
          </Link>
          <button
            type="button"
            className="job-history-refresh-btn"
            onClick={fetchJobs}
            disabled={loading}
            aria-label="Refresh job list"
          >
            <FiRefreshCw className={loading ? 'job-history-spin' : ''} aria-hidden />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <p className="job-history-error" role="alert">
          {error}
        </p>
      )}

      {!error && jobs.length === 0 && !loading && (
        <div className="job-history-empty">
          <FiClock className="job-history-empty-icon" aria-hidden />
          <p>No bulk upload jobs yet.</p>
          <Link to="/dashboard/cms/bulk-upload" className="job-history-empty-link">
            Upload a CSV
          </Link>
        </div>
      )}

      {!error && jobs.length > 0 && (
        <>
          <div className="job-history-table-card">
            <div className="job-history-table-scroll">
              <table ref={tableRef} className="job-history-table">
                <colgroup>
                  {columnWidths.map((w, i) => (
                    <col key={i} style={{ width: `${w}%`, minWidth: `${MIN_COLUMN_PERCENTS[i]}%` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th>
                      <span>Job ID</span>
                      <span
                        className="job-history-resize-handle"
                        onMouseDown={(e) => handleResizeStart(0, e)}
                        role="separator"
                        aria-label="Resize column"
                      />
                    </th>
                    <th>
                      <span>Status</span>
                      <span
                        className="job-history-resize-handle"
                        onMouseDown={(e) => handleResizeStart(1, e)}
                        role="separator"
                        aria-label="Resize column"
                      />
                    </th>
                    <th>
                      <span>Progress</span>
                      <span
                        className="job-history-resize-handle"
                        onMouseDown={(e) => handleResizeStart(2, e)}
                        role="separator"
                        aria-label="Resize column"
                      />
                    </th>
                    <th>
                      <span>Total</span>
                      <span
                        className="job-history-resize-handle"
                        onMouseDown={(e) => handleResizeStart(3, e)}
                        role="separator"
                        aria-label="Resize column"
                      />
                    </th>
                    <th>
                      <span>Success</span>
                      <span
                        className="job-history-resize-handle"
                        onMouseDown={(e) => handleResizeStart(4, e)}
                        role="separator"
                        aria-label="Resize column"
                      />
                    </th>
                    <th>
                      <span>Failed</span>
                      <span
                        className="job-history-resize-handle"
                        onMouseDown={(e) => handleResizeStart(5, e)}
                        role="separator"
                        aria-label="Resize column"
                      />
                    </th>
                    <th>
                      <span>Created</span>
                      <span
                        className="job-history-resize-handle"
                        onMouseDown={(e) => handleResizeStart(6, e)}
                        role="separator"
                        aria-label="Resize column"
                      />
                    </th>
                    <th>
                      <span>Input</span>
                      <span
                        className="job-history-resize-handle"
                        onMouseDown={(e) => handleResizeStart(7, e)}
                        role="separator"
                        aria-label="Resize column"
                      />
                    </th>
                    <th>
                      <span>Output</span>
                      <span
                        className="job-history-resize-handle"
                        onMouseDown={(e) => handleResizeStart(8, e)}
                        role="separator"
                        aria-label="Resize column"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="job-history-empty-cell">
                        No jobs match your search.
                      </td>
                    </tr>
                  ) : (
                    paginated.map((job) => {
                      const progress = Math.min(100, Math.max(0, num(job.progress_percent)));
                      const total = num(job.total_count);
                      const success = num(job.success_count);
                      const failed = num(job.failure_count);
                      return (
                        <tr key={job.job_id}>
                          <td className="job-history-td-id" title={job.job_id || ''}>
                            <span className="job-history-id-cell">
                              <span className="job-history-job-id-text">{job.job_id || '—'}</span>
                              {job.job_id && (
                                <button
                                  type="button"
                                  className="job-history-copy-btn"
                                  onClick={() => handleCopy(job.job_id, `id-${job.job_id}`)}
                                  title="Copy Job ID"
                                  aria-label="Copy Job ID"
                                >
                                  {copiedId === `id-${job.job_id}` ? (
                                    <span className="job-history-copied">Copied!</span>
                                  ) : (
                                    <FiCopy className="job-history-copy-icon" aria-hidden />
                                  )}
                                </button>
                              )}
                            </span>
                          </td>
                          <td>
                            <span className={`job-history-status job-history-status-${(job.status || '').toLowerCase()}`}>
                              {job.status || '—'}
                            </span>
                          </td>
                          <td className="job-history-td-progress">
                            <div className="job-history-progress-wrap">
                              <div className="job-history-progress-track" aria-hidden>
                                <div
                                  className="job-history-progress-fill"
                                  style={{ width: `${progress}%` }}
                                  role="progressbar"
                                  aria-valuenow={progress}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                  aria-label={`${progress}% complete`}
                                />
                              </div>
                              <span className="job-history-progress-value">{progress}%</span>
                            </div>
                          </td>
                          <td className="job-history-td-num">{total}</td>
                          <td className="job-history-td-success">{success}</td>
                          <td className="job-history-td-failed">{failed}</td>
                          <td className="job-history-td-date">
                            <time dateTime={job.created_at}>{formatDate(job.created_at)}</time>
                          </td>
                          <td className="job-history-td-download">
                            {(job.input_download_url || job.s3_input_key) ? (
                              <span className="job-history-download-cell">
                                <a
                                  href={job.input_download_url || job.s3_input_key}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                  className="job-history-download-link"
                                  title="Download input file"
                                >
                                  <FiDownload className="job-history-download-icon" aria-hidden />
                                  <span className="job-history-download-label">Download</span>
                                </a>
                                {job.s3_input_key && (
                                  <button
                                    type="button"
                                    className="job-history-copy-btn"
                                    onClick={() => handleCopy(job.s3_input_key, `input-${job.job_id}`)}
                                    title="Copy S3 path"
                                    aria-label="Copy input S3 path"
                                  >
                                    {copiedId === `input-${job.job_id}` ? (
                                      <span className="job-history-copied">Copied!</span>
                                    ) : (
                                      <FiCopy className="job-history-copy-icon" aria-hidden />
                                    )}
                                  </button>
                                )}
                              </span>
                            ) : (
                              <span className="job-history-na">—</span>
                            )}
                          </td>
                          <td className="job-history-td-download">
                            {(job.output_download_url || job.s3_output_key) ? (
                              <span className="job-history-download-cell">
                                <a
                                  href={job.output_download_url || job.s3_output_key}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                  className="job-history-download-link"
                                  title="Download output file"
                                >
                                  <FiDownload className="job-history-download-icon" aria-hidden />
                                  <span className="job-history-download-label">Download</span>
                                </a>
                                {job.s3_output_key && (
                                  <button
                                    type="button"
                                    className="job-history-copy-btn"
                                    onClick={() => handleCopy(job.s3_output_key, `output-${job.job_id}`)}
                                    title="Copy S3 path"
                                    aria-label="Copy output S3 path"
                                  >
                                    {copiedId === `output-${job.job_id}` ? (
                                      <span className="job-history-copied">Copied!</span>
                                    ) : (
                                      <FiCopy className="job-history-copy-icon" aria-hidden />
                                    )}
                                  </button>
                                )}
                              </span>
                            ) : (
                              <span className="job-history-na">NA</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalItems > 0 && (
            <nav className="job-history-pagination" aria-label="Job list pagination">
              <button
                type="button"
                className="job-history-pagination-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <FiChevronLeft aria-hidden />
              </button>
              <span className="job-history-pagination-info">
                {displayStart}–{displayEnd} of {totalItems}
              </span>
              <button
                type="button"
                className="job-history-pagination-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                <FiChevronRight aria-hidden />
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

export default JobHistory;
